export type AgentStatus = 'Enabled' | 'Disabled'
export type SparkStatus = AgentStatus // backward-compat alias

export const AGENT_CATEGORIES = [
  'Insights & Analytics',
  'Operations',
  'Configuration',
  'Compliance',
  'Customer Service',
  'Other',
] as const
export const SPARK_CATEGORIES = AGENT_CATEGORIES // backward-compat alias

export type AgentCategory = (typeof AGENT_CATEGORIES)[number]
export type SparkCategory = AgentCategory // backward-compat alias

export interface ModelConfigurationRef {
  id: string
  modelName: string
  provider: string
}

export interface AgentListItem {
  id: string
  displayName: string
  description: string | null
  category: AgentCategory
  avatarType: 'emoji' | 'image'
  avatarImageUrl: string | null
  status: AgentStatus
  isMambuBuilt: boolean
  isReadOnly: boolean
  runtimeVersion: number
  modelConfiguration: ModelConfigurationRef | null
  lastModifiedDate: string
}
export type SparkListItem = AgentListItem // backward-compat alias

export interface AgentListResponse {
  items: AgentListItem[]
  nextCursor: string | null
}
export type SparkListResponse = AgentListResponse // backward-compat alias

export interface CreateAgentRequest {
  displayName: string
  category: AgentCategory
}
export type CreateSparkRequest = CreateAgentRequest // backward-compat alias

export interface AgentCreated {
  id: string
  displayName: string
  status: AgentStatus
}
export type SparkCreated = AgentCreated // backward-compat alias

export interface AgentCore {
  id: string
  displayName: string
  description: string | null
  category: AgentCategory
  avatarType: 'emoji' | 'image'
  avatarImageUrl: string | null
  status: AgentStatus
  isMambuBuilt: boolean
  isReadOnly: boolean
  runtimeVersion: number
  modelConfigurationId: string
  creationDate: string
  lastModifiedDate: string
  lastModifiedBy: string
  version: number
}
export type SparkCore = AgentCore // backward-compat alias

export interface AgentDnaFields {
  purpose: string | null
  behavior: string | null
  domainContext: string | null
}
export type SparkDnaFields = AgentDnaFields // backward-compat alias

export interface AgentDetailResponse {
  agent: AgentCore
  dna: AgentDnaFields
  skills: SkillsRef
  connections?: ConnectionsRef
  knowledgeSources?: KnowledgeSourcesRef
  guardrails?: GuardrailConfig
  access?: AccessRef
}
export type SparkDetailResponse = AgentDetailResponse // backward-compat alias

export interface AgentUpdate {
  version: number
  changedSection?: string
  identity?: {
    displayName?: string
    description?: string | null
    category?: AgentCategory
    avatarType?: 'emoji' | 'image'
    avatarImageUrl?: string | null
  }
  dna?: {
    purpose?: string | null
    behavior?: string | null
    domainContext?: string | null
  }
  modelConfigurationId?: string
  skills?: SkillsRef
  connections?: ConnectionsRef
  knowledgeSources?: KnowledgeSourcesRef
  guardrails?: GuardrailConfig
  access?: AccessRef
}
export type SparkUpdate = AgentUpdate // backward-compat alias

/* ─── Guardrail types ─── */

export interface GuardrailConfig {
  requireHumanConfirmation: boolean
  escalateOnUncertainty: boolean
  monetaryApprovalThreshold: string | null
  monetaryApprovalCurrency: string | null
  allowBulkOperations: boolean
  allowOutOfHoursActions: boolean
  dryRunMode: boolean
  sessionTimeoutMinutes: number
  restrictToAgentScope: boolean
  showConfidenceScores: boolean
  showDataSourceCitations: boolean
  allowInternetAccess: boolean
  responseLanguage: string
}

export interface GuardrailFormState {
  requireHumanConfirmation: boolean
  escalateOnUncertainty: boolean
  monetaryApprovalThreshold: string
  monetaryApprovalCurrency: string
  allowBulkOperations: boolean
  allowOutOfHoursActions: boolean
  dryRunMode: boolean
  sessionTimeoutMinutes: number
  restrictToAgentScope: boolean
  showConfidenceScores: boolean
  showDataSourceCitations: boolean
  allowInternetAccess: boolean
  responseLanguage: string
}

export interface AvatarUploadResponse {
  avatarImageUrl: string
}

export interface AgentStatusResponse {
  status: AgentStatus
  runtimeVersion: number
  lastModifiedDate: string
}
export type SparkStatusResponse = AgentStatusResponse // backward-compat alias

/* ─── Skill types ─── */

export type SkillType = 'Read' | 'Execute'
export type SkillScope = 'Local' | 'Global' | 'Mambu'

export interface SkillRef {
  skillId: string
  isEnabled: boolean
}

export interface SkillsRef {
  local: SkillRef[]
  global: SkillRef[]
  mambu: SkillRef[]
}

export interface Skill {
  id: string
  name: string
  type: SkillType
  scope: SkillScope
  ownerAgentId?: string | null
  ownerSparkId: string | null // backward-compat alias
  definition: string | null
  shortDescription: string | null
  agentSummary: string | null
  isEnabled: boolean
  creationDate: string
  lastModifiedDate: string
  lastModifiedBy: string
}

export interface SkillListResponse {
  items: Skill[]
  nextCursor: string | null
}

export interface SkillCreate {
  name: string
  type: SkillType
  scope: SkillScope
  ownerAgentId?: string
  ownerSparkId?: string // backward-compat alias
  definition?: string | null
  shortDescription?: string | null
  agentSummary: string
}

export interface SkillUpdate {
  name: string
  type: SkillType
  definition?: string | null
  shortDescription?: string | null
  agentSummary: string
}

export interface SkillDependent {
  agentId: string
  agentDisplayName: string
  sparkId: string // backward-compat alias
  sparkDisplayName: string // backward-compat alias
  isEnabled: boolean
}

export interface SkillDependentsResponse {
  items: SkillDependent[]
}

/* ─── Connection types ─── */

export type ConnectionType = 'MCP'
export type ConnectionAuthMethod = 'None' | 'OAuth2' | 'APIKey'
export type DiscoveryPattern = 'StandardTools' | 'SearchInvoke'
export type OperationType = 'Read' | 'Execute'

export interface ConnectionOperation {
  id?: string | null
  key: string
  name: string
  category: string
  type: OperationType
  isEnabled: boolean
  isDeprecated: boolean
  lastDiscoveredAt?: string | null
}

export interface Connection {
  id: string
  name: string
  description: string | null
  type: ConnectionType
  operationDiscoveryPattern: DiscoveryPattern
  endpointUrl: string
  authMethod: ConnectionAuthMethod
  authConfig: Record<string, unknown>
  isEnabled: boolean
  isEssential: boolean
  lastDiscoveryAt: string | null
  version: number
  operations: ConnectionOperation[]
  lastHealthStatus: 'healthy' | 'unreachable' | null
}

export interface ConnectionListResponse {
  items: Connection[]
  nextCursor: string | null
}

export interface ConnectionCreate {
  name: string
  description?: string | null
  type: ConnectionType
  operationDiscoveryPattern: DiscoveryPattern
  endpointUrl: string
  authMethod: ConnectionAuthMethod
  authConfig?: Record<string, unknown>
  operations?: Array<{ key: string; name: string; category: string; type: OperationType }>
}

export interface ConnectionUpdate {
  name: string
  description?: string | null
  operationDiscoveryPattern: DiscoveryPattern
  endpointUrl: string
  authMethod: ConnectionAuthMethod
  authConfig?: Record<string, unknown>
  operations?: Array<{ id?: string; key: string; name: string; category: string; type: OperationType }>
}

export interface DiscoverRequest {
  endpointUrl?: string
  authMethod?: ConnectionAuthMethod
  authConfig?: Record<string, unknown>
  connectionId?: string
  operationDiscoveryPattern?: DiscoveryPattern
}

export interface DiscoverResponse {
  operations: ConnectionOperation[]
  discoveredAt: string
}

/* ─── Per-Agent connection bindings ─── */

export interface ConnectionOperationRef {
  operationId: string
  isEnabled: boolean
}

export interface ConnectionBindingRef {
  connectionId: string
  isEnabled: boolean
  operations: ConnectionOperationRef[]
}

export type ConnectionsRef = ConnectionBindingRef[]

export interface ConnectionDependent {
  agentId: string
  agentDisplayName: string
  sparkId: string // backward-compat alias
  sparkDisplayName: string // backward-compat alias
}

export interface ConnectionHealth {
  status: 'healthy' | 'unreachable'
  latencyMs: number
  checkedAt: string
  fingerprintChanged: boolean
}

/* ─── Knowledge Source types ─── */

export type KnowledgeSourceType = 'Document' | 'Web'
export type KnowledgeSourceScope = 'Local' | 'Global'
export type ProbeStatus = 'ok' | 'warn' | 'error'

export interface KnowledgeSourceRef {
  knowledgeSourceId: string
  isEnabled: boolean
}

export interface KnowledgeSourcesRef {
  local: KnowledgeSourceRef[]
  global: KnowledgeSourceRef[]
}

export interface KnowledgeSource {
  id: string
  sourceType: KnowledgeSourceType
  scope: KnowledgeSourceScope
  displayName: string
  agentSummary: string | null
  mimeType: string | null
  fileSize: number | null
  isIngested: boolean
  isSyncing: boolean
  ingestionLastError: string | null
  url: string | null
  urlReachable: boolean | null
  lastProbeStatus: ProbeStatus | null
  lastProbeAt: string | null
  creationDate: string
  lastModifiedDate: string
  lastModifiedBy: string
}

export interface KnowledgeSourceListResponse {
  items: KnowledgeSource[]
  nextCursor: string | null
}

export interface KnowledgeSourceDependent {
  agentId: string
  agentDisplayName: string
  sparkId: string // backward-compat alias
  sparkDisplayName: string // backward-compat alias
  isEnabled: boolean
}

/* ─── Access types ─── */

export type PrincipalType = 'User' | 'Group'

export interface AccessPermission {
  principalId: string
  principalType: PrincipalType
  principalName: string
  principalEmail: string | null
  grantedAt: string | null
}

export interface AccessRef {
  permissions: AccessPermission[]
  defaultAccessConfig: {
    grantsInteractionToAllTenantUsers: boolean
  }
  confirmDefaultAccessToggle?: boolean
}

export interface PrincipalSearchItem {
  principalType: PrincipalType
  principalId: string
  principalName: string
  principalEmail: string | null
  principalUsername: string | null
}

export interface PrincipalSearchResponse {
  items: PrincipalSearchItem[]
}

/* ─── Model catalog types ─── */

export type ModelStatus = 'Active' | 'Inactive'

export interface ModelConfiguration {
  id: string
  modelName: string
  provider: string
  isDefault: boolean
}

export interface ModelConfigurationListResponse {
  items: ModelConfiguration[]
}

/* ─── Global Settings types ─── */

export interface GlobalModelAvailability {
  modelId: string
  status: ModelStatus
}

export interface GlobalModels {
  defaultModelId: string
  availability: GlobalModelAvailability[]
}

export type GlobalPermissionLevel = 'Admin' | 'Editor'

export interface GlobalAccessPermission {
  principalId: string
  principalType: PrincipalType
  principalName: string
  principalEmail: string | null
  permissionLevel: GlobalPermissionLevel
  grantedAt: string | null
}

export interface GlobalSettingsAccess {
  permissions: GlobalAccessPermission[]
}

/* `guardrails`, `access`, `connections` and `models` are
 * Admin-only and come back `null` — never `[]`/an empty object — when withheld from
 * an Editor caller (MS-741 §3.1 GET scoping). `version`, `skills` and
 * `knowledgeSources` stay always-populated: every Studio role may see them. Never
 * conflate a withheld (`null`) section with a genuinely empty one. */
export interface GlobalSettingsResponse {
  version: number
  guardrails: GuardrailConfig | null
  access: GlobalSettingsAccess | null
  skills: SkillRef[]
  connections: ConnectionBindingRef[] | null
  knowledgeSources: KnowledgeSourceRef[]
  models: GlobalModels | null
}

export type GlobalSettingsUpdate = GlobalSettingsResponse

/* ─── Audit Log types ─── */

export type AuditSeverity = 'low' | 'medium' | 'high'

export interface AuditLogEntry {
  id: string
  occurredAt: string
  actorSub: string
  actorEmail: string | null
  actorDisplayName: string | null
  action: string
  resourceType: string
  resourceId: string
  resourceDisplayName: string | null
  section: string | null
  setting: string | null
  changeDescription: string
  beforeState: Record<string, unknown> | null
  afterState: Record<string, unknown> | null
  severity: AuditSeverity
}

export interface AuditLogListResponse {
  items: AuditLogEntry[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}

export interface AuditLogActor {
  sub: string
  displayName: string
}

export interface AuditLogActorsResponse {
  items: AuditLogActor[]
}

export interface AuditLogStats {
  changesLast30Days: number
  activeUsers: number
  lastChangeAt: string | null
  mostChangedAgent: { agentId: string; displayName: string } | null
  mostChangedSpark: { agentId: string; displayName: string } | null // backward-compat alias
}

/* ─── Mambu MCP types ─── */

export type MambuConnectionStatus = 'live' | 'preview'

export interface MambuConnectionCatalogItem {
  id: string | null
  key: string
  name: string
  description: string | null
  status: MambuConnectionStatus
  isEnabled: boolean
}

export interface MambuConnectionCatalogResponse {
  items: MambuConnectionCatalogItem[]
}

export interface MambuConnectionOperation {
  id: string
  key: string
  name: string
  category: string
  type: OperationType
  isEnabled: boolean
  /** Optional so an older API (which omits the field) degrades to an empty Deprecated group. */
  isDeprecated?: boolean
}

export interface MambuConnectionDetail {
  id: string
  key: string
  name: string
  description: string | null
  status: MambuConnectionStatus
  remoteEndpoint: string
  isEnabled: boolean
  version: number
  defaultPermissionTemplateId: string | null
  operations: MambuConnectionOperation[]
}

export interface MambuConnectionOperationToggle {
  operationId: string
  isEnabled: boolean
}

export interface MambuConnectionUpdate {
  isEnabled: boolean
  defaultPermissionTemplateId: string | null
  operations: MambuConnectionOperationToggle[]
}

export interface PermissionTemplateOperation {
  operationId: string
  isEnabled: boolean
}

export interface PermissionTemplateCreate {
  name: string
  operations: PermissionTemplateOperation[]
}

export type PermissionTemplateUpdate = PermissionTemplateCreate

export interface PermissionTemplate {
  id: string
  connectionId: string
  name: string
  version: number
  operations: PermissionTemplateOperation[]
}

export interface PermissionTemplateListResponse {
  items: PermissionTemplate[]
}

export interface UserAccessOperation {
  operationId: string
  isEnabled: boolean
}

export interface UserAccessCreate {
  principalSub: string
  principalDisplayName: string
  principalEmail: string | null
  templateId: string | null
  operations: UserAccessOperation[]
}

export interface UserAccessUpdate {
  templateId: string | null
  operations: UserAccessOperation[]
}

export interface UserAccess {
  id: string
  connectionId: string
  principalSub: string
  principalDisplayName: string
  principalEmail: string | null
  templateId: string | null
  grantedAt: string
  version: number
  operations: UserAccessOperation[]
  droppedOperationIds: string[]
}

export interface UserAccessListResponse {
  items: UserAccess[]
}

/* ─── Features types ─── */

export interface FeaturesResponse {
  allowCustomAgents: boolean
  allowCustomSparks: boolean // backward-compat alias
}

/* ─── MCP Analytics read path (MS-701 / MS-702) ───
   Mirrors src/studio/api/schemas/mcp_analytics.py. `channel` is exactly
   'agent' | 'external' — only the literal client id "agent" maps to the agent
   channel — and the `users` filter carries EMAIL, not sub: the service resolves
   email→sub before any roll-up query because email never enters a roll-up grain. */

export interface McpAnalyticsInvocation {
  eventId: string
  timestamp: string
  sub: string
  email: string
  name: string
  channel: 'agent' | 'external'
  /** Empty until mmcp lands MS-697; the Channel cell's `· <agent>` suffix reads it. */
  agentName: string
  invocationType: string
  invocationMode: string
  responseStatus: string
  latencyMs: number
  descriptionItems: string[]
}

export interface McpAnalyticsInvocationListResponse {
  items: McpAnalyticsInvocation[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}

export interface McpAnalyticsSeriesPoint {
  bucket: string
  invocationType: string
  invocations: number
  errors: number
  sumLatencyMs: number
  minLatencyMs: number | null
  maxLatencyMs: number | null
}

export interface McpAnalyticsSeriesResponse {
  granularity: string
  timezone: string
  /** Which table answered — 'rollups/activity' | 'rollups/user'. The first thing to
   *  look at when a chart disagrees with the table below it. */
  source: string
  /** The continuous bucket spine over [from, to], empty buckets included. */
  buckets: string[]
  points: McpAnalyticsSeriesPoint[]
}

export interface McpAnalyticsSearchActivity {
  eventId: string
  timestamp: string
  sub: string
  email: string
  name: string
  channel: 'agent' | 'external'
  agentName: string
  invocationMode: string
  responseStatus: string
  searchInput: string[]
  /** len(returnedOperationKeys) — NOT totalRecordsReturned. Never conflate them. */
  matchedOperations: number
  totalRecordsReturned: number
  returnedOperationKeys: string[]
}

export interface McpAnalyticsSearchActivityListResponse {
  items: McpAnalyticsSearchActivity[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}

export interface McpAnalyticsInvokeActivityItem {
  operationKey: string
  invocations: number
  errors: number
  avgLatencyMs: number | null
}

export interface McpAnalyticsInvokeActivityResponse {
  items: McpAnalyticsInvokeActivityItem[]
  generatedAt: string
}

export interface McpAnalyticsUser {
  sub: string
  /** The value the User filter sends. */
  email: string
  name: string
}

export interface McpAnalyticsUsersResponse {
  items: McpAnalyticsUser[]
}
